import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, ChevronRight, Eye, FileDown, FileText, GripVertical, SlidersHorizontal, X, BarChart3, Target, Wallet, TrendingUp, TrendingDown, Sparkles, Receipt } from 'lucide-react';
import { formatCurrency } from '../../utils/format';
import { getCompletionColor } from '../../utils/colors';
import NegotiatorDetailsModal from './NegotiatorDetailsModal';
import DirectorDetailsModal from './DirectorDetailsModal';
import ColumnConfigPanel, { ColumnConfig } from './ColumnConfigPanel';
import { useNegotiatorTransactions } from '../../hooks/useNegotiatorTransactions';
import { NegotiatorTransaction } from '../../types/transaction';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { downloadStyledExcel, CURRENCY_FMT, PCT_FMT } from '../../utils/excelExport';
import { getMonthObjectiveLabel, getMonthRangeDates, getMonthsLabel, getPeriodEndDate, getPeriodRanges, isPotentialVisible } from '../../utils/period';

// ── Types ──
interface Negotiator {
  id: string;
  name: string;
  code: string;
  total_sales: number;
  potential_sales: number;
  sales_count: number;
  annual_objective: number;
}

interface Agency {
  name: string;
  code: string;
  total_sales: number;
  potential_sales: number;
  sales_count: number;
  annual_objective: number;
  accounting_objective: number;
  negotiators: Negotiator[];
}

interface Director {
  id: string;
  name: string;
  code: string;
  company: string;
  total_sales: number;
  potential_sales: number;
  sales_count: number;
  annual_objective: number;
  accounting_objective: number;
  agencies: Agency[];
}

interface DirectionTableProps {
  directors: Director[];
  period: 'year' | 'quarter' | 'month';
  selectedPeriod: string;
  fiscalYear: number;
}

// ── Column config ──
const STORAGE_KEY = 'kprod-direction-columns';

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { id: 'structure',   label: 'Structure',       visible: true, fixed: true },
  { id: 'objective',   label: 'Objectif',        visible: true },
  { id: 'potential',   label: 'Hono Potentiel',  visible: true },
  { id: 'sales',       label: 'Hono Réalisé',    visible: true },
  { id: 'cumulative',  label: 'Hono Cumulé',     visible: true },
  { id: 'remaining',   label: 'Reste',           visible: true },
  { id: 'completion',  label: '% Réalisation',   visible: true },
  { id: 'transactions',label: 'Nb Trans.',        visible: true },
];

function loadColumnsFromStorage(): ColumnConfig[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMNS.map(c => ({ ...c }));
    const saved: ColumnConfig[] = JSON.parse(raw);
    const defaultMap = new Map(DEFAULT_COLUMNS.map(c => [c.id, c]));
    const merged = saved
      .filter(c => defaultMap.has(c.id))
      .map(c => ({ ...defaultMap.get(c.id)!, visible: c.visible }));
    const savedIds = new Set(saved.map(c => c.id));
    DEFAULT_COLUMNS.filter(c => !savedIds.has(c.id)).forEach(c => merged.push({ ...c }));
    return merged;
  } catch {
    return DEFAULT_COLUMNS.map(c => ({ ...c }));
  }
}

// ── Company groups (ordered) ──
const COMPANY_GROUPS = [
  {
    key: 'KI',
    label: 'Kermarrec Immobilier',
    headerBg: 'bg-blue-50',
    headerBorder: 'border-blue-200',
    textClass: 'text-blue-700',
    dotClass: 'bg-blue-700',
  },
  {
    key: 'DRYADE',
    label: 'Dryade',
    headerBg: 'bg-violet-50',
    headerBorder: 'border-violet-200',
    textClass: 'text-violet-700',
    dotClass: 'bg-violet-700',
  },
  {
    key: 'KA',
    label: 'Kermarrec Atlantique',
    headerBg: 'bg-emerald-50',
    headerBorder: 'border-emerald-200',
    textClass: 'text-emerald-700',
    dotClass: 'bg-emerald-700',
  },
] as const;

// ── Visual helpers (module-level, no hooks) ──
function getCompanyBg(company: string) {
  switch (company) {
    case 'KI':     return 'bg-blue-700';
    case 'KA':     return 'bg-emerald-700';
    case 'DRYADE': return 'bg-violet-700';
    default:       return 'bg-slate-600';
  }
}

function getCompanyBadgeClass(company: string) {
  switch (company) {
    case 'KI':     return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'KA':     return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'DRYADE': return 'bg-violet-50 text-violet-700 border-violet-200';
    default:       return 'bg-slate-50 text-slate-700 border-slate-200';
  }
}

function getInitials(name: string) {
  const words = name.trim().split(/\s+/);
  return words.length >= 2
    ? (words[0][0] + words[1][0]).toUpperCase()
    : name.substring(0, 2).toUpperCase();
}

function moneyCell(value: number, pillClass: string) {
  if (!value || value === 0) return <span className="text-slate-300 text-sm">—</span>;
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${pillClass}`}>
      {formatCurrency(value)}
    </span>
  );
}

export default function DirectionTable({ directors, period, selectedPeriod, fiscalYear }: DirectionTableProps) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [selectedNegotiator, setSelectedNegotiator] = useState<Negotiator | null>(null);
  const [selectedDirector, setSelectedDirector] = useState<Director | null>(null);
  const [transactions, setTransactions] = useState<NegotiatorTransaction[]>([]);
  const [selectedAgencyName, setSelectedAgencyName] = useState<string | undefined>();
  const { loadTransactions } = useNegotiatorTransactions();
  const [exporting, setExporting] = useState(false);
  const [columns, setColumns] = useState<ColumnConfig[]>(loadColumnsFromStorage);
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const columnPanelRef = useRef<HTMLDivElement>(null);

  // ── Reorder state ──
  const [directorOrder, setDirectorOrder] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('kprod-direction-director-order') || '[]'); }
    catch { return []; }
  });
  const [agencyOrder, setAgencyOrder] = useState<Record<string, string[]>>(() => {
    try { return JSON.parse(localStorage.getItem('kprod-direction-agency-order') || '{}'); }
    catch { return {}; }
  });

  useEffect(() => {
    localStorage.setItem('kprod-direction-director-order', JSON.stringify(directorOrder));
  }, [directorOrder]);

  useEffect(() => {
    localStorage.setItem('kprod-direction-agency-order', JSON.stringify(agencyOrder));
  }, [agencyOrder]);

  const sortedDirectors = useMemo(() => {
    if (directorOrder.length === 0) return directors;
    return [...directors].sort((a, b) => {
      const ai = directorOrder.indexOf(a.id);
      const bi = directorOrder.indexOf(b.id);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [directors, directorOrder]);

  const getSortedAgencies = (director: Director) => {
    const order = agencyOrder[director.id];
    if (!order || order.length === 0) return director.agencies;
    return [...director.agencies].sort((a, b) => {
      const ai = order.indexOf(a.code);
      const bi = order.indexOf(b.code);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  };

  // ── Selection state ──
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showRecapModal, setShowRecapModal] = useState(false);

  const toggleSelection = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedItems(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Drag state ──
  const [draggingDirId, setDraggingDirId] = useState<string | null>(null);
  const [dragOverDirId, setDragOverDirId] = useState<string | null>(null);
  const [draggingAgency, setDraggingAgency] = useState<{ directorId: string; code: string } | null>(null);
  const [dragOverAgencyCode, setDragOverAgencyCode] = useState<string | null>(null);

  // Director drag handlers
  const onDirDragStart = (id: string) => setDraggingDirId(id);
  const onDirDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    if (id !== draggingDirId) setDragOverDirId(id);
  };
  const onDirDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggingDirId || draggingDirId === targetId) return;
    const base = directorOrder.length > 0 ? directorOrder : directors.map(d => d.id);
    const from = base.indexOf(draggingDirId);
    const to = base.indexOf(targetId);
    if (from === -1 || to === -1) return;
    const next = [...base];
    next.splice(from, 1);
    next.splice(to, 0, draggingDirId);
    setDirectorOrder(next);
    setDraggingDirId(null);
    setDragOverDirId(null);
  };
  const onDirDragEnd = () => { setDraggingDirId(null); setDragOverDirId(null); };

  // Agency drag handlers
  const onAgcDragStart = (directorId: string, code: string) =>
    setDraggingAgency({ directorId, code });
  const onAgcDragOver = (e: React.DragEvent, code: string) => {
    e.preventDefault();
    if (code !== draggingAgency?.code) setDragOverAgencyCode(code);
  };
  const onAgcDrop = (e: React.DragEvent, directorId: string, targetCode: string) => {
    e.preventDefault();
    if (!draggingAgency || draggingAgency.code === targetCode || draggingAgency.directorId !== directorId) return;
    const director = directors.find(d => d.id === directorId);
    if (!director) return;
    const base = agencyOrder[directorId]?.length > 0
      ? agencyOrder[directorId]
      : director.agencies.map(a => a.code);
    const from = base.indexOf(draggingAgency.code);
    const to = base.indexOf(targetCode);
    if (from === -1 || to === -1) return;
    const next = [...base];
    next.splice(from, 1);
    next.splice(to, 0, draggingAgency.code);
    setAgencyOrder(prev => ({ ...prev, [directorId]: next }));
    setDraggingAgency(null);
    setDragOverAgencyCode(null);
  };
  const onAgcDragEnd = () => { setDraggingAgency(null); setDragOverAgencyCode(null); };

  useEffect(() => {
    setExpandedItems(new Set());
  }, [directors]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
  }, [columns]);

  useEffect(() => {
    if (!showColumnPanel) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (columnPanelRef.current && !columnPanelRef.current.contains(e.target as Node)) {
        setShowColumnPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnPanel]);

  const hiddenCount = columns.filter(c => !c.visible).length;
  const visibleColumns = columns.filter(c => c.visible);

  const showPotential = isPotentialVisible(getPeriodEndDate(period, selectedPeriod, fiscalYear));

  // ── Period helpers ──
  const getFormattedPeriod = () => {
    if (period === 'year') return `Année ${fiscalYear}-${fiscalYear + 1}`;
    if (period === 'quarter') {
      const quarterMap: Record<string, string> = {
        '1': 'Trimestre 1 (Avril - Juin)',
        '2': 'Trimestre 2 (Juillet - Septembre)',
        '3': 'Trimestre 3 (Octobre - Décembre)',
        '4': 'Trimestre 4 (Janvier - Mars)',
      };
      const year = selectedPeriod === '4' ? fiscalYear + 1 : fiscalYear;
      return `${quarterMap[selectedPeriod]} ${year}`;
    }
    return getMonthsLabel(selectedPeriod, fiscalYear);
  };

  // L'objectif reçu est déjà pro-ratisé pour la période par useDirectionStats
  const calculateObjective = (annualObjective: number) => annualObjective;

  const getObjectiveLabel = () =>
    period === 'year' ? 'Objectif Annuel' : period === 'quarter' ? 'Objectif Trim.' : `Objectif ${getMonthObjectiveLabel(selectedPeriod)}`;

  const toggleItem = (itemId: string) => {
    const next = new Set(expandedItems);
    next.has(itemId) ? next.delete(itemId) : next.add(itemId);
    setExpandedItems(next);
  };

  // ── Exports ──
  const handleExportExcel = async () => {
    const objLabel = getObjectiveLabel();
    const columns = [
      { header: 'Structure',      key: 'name',      width: 32 },
      { header: 'Niveau',         key: 'level',     width: 14 },
      { header: 'Société',        key: 'company',   width: 10, align: 'center' as const },
      { header: objLabel,         key: 'obj',       width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: 'Hono Potentiel', key: 'potential', width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: 'Hono Réalisé',   key: 'sales',     width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: 'Hono Cumulé',    key: 'cumul',     width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: 'Reste',          key: 'remaining', width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: '% Réalisation',  key: 'rate',      width: 14, numFmt: PCT_FMT,      align: 'center' as const },
      { header: 'Nb Trans.',      key: 'trans',     width: 10, align: 'center' as const },
    ];

    const makeRow = (name: string, level: string, company: string, obj: number, potential: number, sales: number, trans: number, rowLevel: 0 | 1 | 2) => {
      const o = calculateObjective(obj);
      const rate = o ? parseFloat(((sales / o) * 100).toFixed(1)) : 0;
      return {
        data: { name, level, company, obj: o, potential, sales, cumul: potential + sales, remaining: o - sales, rate, trans },
        level: rowLevel,
      };
    };

    const rows: { data: Record<string, string | number>; level: 0 | 1 | 2 }[] = [];
    sortedDirectors.forEach(d => {
      rows.push(makeRow(d.name, 'Direction', d.company, d.annual_objective, d.potential_sales, d.total_sales, d.sales_count, 0));
      getSortedAgencies(d).forEach(a => {
        rows.push(makeRow(a.name, 'Agence', d.company, a.annual_objective, a.potential_sales, a.total_sales, a.sales_count, 1));
        a.negotiators.forEach(n => {
          rows.push(makeRow(n.name, 'Négociateur', d.company, n.annual_objective, n.potential_sales, n.total_sales, n.sales_count, 2));
        });
      });
    });

    const label = getFormattedPeriod().replace(/[/\s]/g, '_');
    await downloadStyledExcel(`top_position_direction_${label}.xlsx`, 'Par Direction', columns, rows);
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const tables = document.querySelectorAll('.direction-section');
      if (!tables.length) return;
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      let currentY = 10;
      pdf.setFontSize(16);
      pdf.text('Classement - Par Direction', 10, currentY);
      currentY += 10;
      pdf.setFontSize(12);
      pdf.text(getFormattedPeriod(), 10, currentY);
      currentY += 15;
      for (const table of Array.from(tables)) {
        const canvas = await html2canvas(table as HTMLElement, {
          scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff',
        });
        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        if (currentY + pdfHeight > pdf.internal.pageSize.getHeight() - 10) {
          pdf.addPage();
          currentY = 10;
        }
        pdf.addImage(imgData, 'PNG', 10, currentY, pdfWidth, pdfHeight);
        currentY += pdfHeight + 10;
      }
      pdf.save(`top_position_direction_${getFormattedPeriod().replace(/[\/\s]/g, '_')}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  // ── Negotiator click ──
  const handleNegotiatorClick = async (negotiator: Negotiator, agencyName?: string) => {
    let startDate: string, endDate: string;
    if (period === 'year') {
      startDate = `${fiscalYear}-04-01`;
      endDate = `${fiscalYear + 1}-03-31`;
    } else if (period === 'quarter') {
      const quarterMap: Record<string, { start: string; end: string }> = {
        '1': { start: '04', end: '06' }, '2': { start: '07', end: '09' },
        '3': { start: '10', end: '12' }, '4': { start: '01', end: '03' },
      };
      const q = quarterMap[selectedPeriod];
      const year = selectedPeriod === '4' ? fiscalYear + 1 : fiscalYear;
      startDate = `${year}-${q.start}-01`;
      endDate = `${year}-${q.end}-${new Date(year, parseInt(q.end), 0).getDate()}`;
    } else {
      ({ startDate, endDate } = getMonthRangeDates(selectedPeriod, fiscalYear));
    }

    const ranges = getPeriodRanges(period, selectedPeriod, fiscalYear, startDate, endDate);
    setSelectedNegotiator(negotiator);
    setSelectedAgencyName(agencyName);
    // L'agence est transmise : le détail ne montre que les lignes de l'agence cliquée
    const txs = await loadTransactions(negotiator.id, startDate, endDate, agencyName, ranges);
    setTransactions(txs);
  };

  // ── Recap data ──
  interface RecapItem {
    id: string;
    name: string;
    company: string;
    level: 'director' | 'agency' | 'negotiator';
    objective: number;
    potential: number;
    sales: number;
    transactions: number;
  }

  const getRecapItems = (): RecapItem[] => {
    const items: RecapItem[] = [];
    sortedDirectors.forEach(director => {
      if (selectedItems.has(director.id)) {
        items.push({
          id: director.id, name: director.name, company: director.company, level: 'director',
          objective: calculateObjective(director.annual_objective),
          potential: director.potential_sales, sales: director.total_sales, transactions: director.sales_count,
        });
      }
      getSortedAgencies(director).forEach(agency => {
        const agcKey = `${director.id}_${agency.code}`;
        if (selectedItems.has(agcKey)) {
          items.push({
            id: agcKey, name: agency.name, company: director.company, level: 'agency',
            objective: calculateObjective(agency.annual_objective),
            potential: agency.potential_sales, sales: agency.total_sales, transactions: agency.sales_count,
          });
        }
        agency.negotiators.forEach(negotiator => {
          const negKey = `${director.id}_${agency.code}_${negotiator.id}`;
          if (selectedItems.has(negKey)) {
            items.push({
              id: negKey, name: negotiator.name, company: director.company, level: 'negotiator',
              objective: calculateObjective(negotiator.annual_objective),
              potential: negotiator.potential_sales, sales: negotiator.total_sales, transactions: negotiator.sales_count,
            });
          }
        });
      });
    });
    return items;
  };

  // ── Cell renderers ──
  const renderDataCells = (
    objective: number,
    totalSales: number,
    potentialSales: number,
    salesCount: number,
  ) => {
    const periodObj = calculateObjective(objective);
    const rate = periodObj ? (totalSales / periodObj) * 100 : 0;
    const remaining = periodObj - totalSales;

    return visibleColumns
      .filter(c => c.id !== 'structure')
      .map(col => {
        switch (col.id) {
          case 'objective':
            return (
              <td key="objective" className="px-4 py-3 whitespace-nowrap">
                {moneyCell(periodObj, 'border-slate-300 text-slate-600 bg-slate-50')}
              </td>
            );
          case 'potential':
            return (
              <td key="potential" className="px-4 py-3 whitespace-nowrap">
                {showPotential
                  ? moneyCell(potentialSales, 'border-amber-300 text-amber-700 bg-amber-50')
                  : <span className="text-slate-300 text-sm">—</span>}
              </td>
            );
          case 'sales':
            return (
              <td key="sales" className="px-4 py-3 whitespace-nowrap">
                {moneyCell(totalSales, 'border-emerald-300 text-emerald-700 bg-emerald-50')}
              </td>
            );
          case 'cumulative':
            return (
              <td key="cumulative" className="px-4 py-3 whitespace-nowrap">
                {moneyCell(
                  showPotential ? potentialSales + totalSales : totalSales,
                  'border-indigo-300 text-indigo-700 bg-indigo-50'
                )}
              </td>
            );
          case 'remaining':
            return (
              <td key="remaining" className="px-4 py-3 whitespace-nowrap">
                {periodObj ? (
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                    remaining > 0
                      ? 'bg-red-50 text-red-700 border-red-200'
                      : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  }`}>
                    <span>{remaining > 0 ? '↘' : '↗'}</span>
                    {formatCurrency(Math.abs(remaining))}
                  </span>
                ) : <span className="text-slate-300 text-sm">—</span>}
              </td>
            );
          case 'completion':
            return (
              <td key="completion" className="px-4 py-3 whitespace-nowrap">
                {periodObj ? (
                  <div className="flex flex-col gap-1.5">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border w-fit ${
                      rate >= 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      rate >= 75  ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      rate >= 50  ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                    'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {rate.toFixed(1)}%
                    </span>
                    <div className="h-1 w-20 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          rate >= 100 ? 'bg-emerald-400' :
                          rate >= 75  ? 'bg-blue-400' :
                          rate >= 50  ? 'bg-amber-400' : 'bg-red-400'
                        }`}
                        style={{ width: `${Math.min(rate, 100)}%` }}
                      />
                    </div>
                  </div>
                ) : <span className="text-slate-300 text-sm">—</span>}
              </td>
            );
          case 'transactions':
            return (
              <td key="transactions" className="px-4 py-3 whitespace-nowrap">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-xs font-bold text-slate-600 border border-slate-200">
                  {salesCount}
                </span>
              </td>
            );
          default:
            return null;
        }
      });
  };

  // ── Totaux honoraires ──
  const totalObjective  = directors.reduce((s, d) => s + d.annual_objective, 0);
  const totalHR         = directors.reduce((s, d) => s + d.total_sales, 0);
  const totalHP         = directors.reduce((s, d) => s + d.potential_sales, 0);
  const totalRemaining  = totalObjective - totalHR;
  const totalTransactions = directors.reduce((s, d) => s + d.sales_count, 0);

  const tableHead = (
    <thead className="bg-slate-50 border-b border-slate-200">
      <tr>
        {visibleColumns.map(col => {
          const label = col.id === 'objective' ? getObjectiveLabel() : col.label;
          const base = 'px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider';
          if (col.id === 'structure') return <th key="structure" className={`${base} min-w-[200px]`}>Structure</th>;
          return <th key={col.id} className={base}>{label}</th>;
        })}
      </tr>
    </thead>
  );

  return (
    <>
      {/* ── Action bar ── */}
      <div className="relative mb-4" ref={columnPanelRef}>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-3">
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={handleExportExcel}
                disabled={exporting}
                title="Exporter Excel"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                <FileDown size={15} />
              </button>
              <button
                onClick={handleExportPDF}
                disabled={exporting}
                title="Exporter PDF"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                <FileText size={15} />
              </button>
              <div className="w-9" />
            </div>
          </div>
        </div>

        <div className="absolute right-4 bottom-3 z-10">
          <button
            onClick={() => setShowColumnPanel(p => !p)}
            title="Gérer les colonnes"
            className={`relative inline-flex items-center justify-center w-8 h-8 rounded-xl border transition-colors ${
              showColumnPanel
                ? 'bg-[#2d3a5e] text-white border-[#2d3a5e]'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <SlidersHorizontal size={15} />
            {hiddenCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-500 text-white text-[9px] font-bold">
                {hiddenCount}
              </span>
            )}
          </button>
          {showColumnPanel && (
            <div className="absolute right-0 top-full mt-2 z-50">
              <ColumnConfigPanel
                columns={columns}
                onColumnsChange={setColumns}
                defaultColumns={DEFAULT_COLUMNS}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden direction-section">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            {tableHead}
            <tbody className="bg-white">
              {COMPANY_GROUPS.map(group => {
                const groupDirectors = sortedDirectors.filter(d => d.company === group.key);
                if (groupDirectors.length === 0) return null;
                const groupObjective = groupDirectors.reduce((s, d) => s + d.annual_objective, 0);
                const groupHP        = groupDirectors.reduce((s, d) => s + d.potential_sales, 0);
                const groupHR        = groupDirectors.reduce((s, d) => s + d.total_sales, 0);
                const groupTrans     = groupDirectors.reduce((s, d) => s + d.sales_count, 0);
                return (
                  <React.Fragment key={group.key}>
                    {/* ── Group aggregate row ── */}
                    <tr className={`${group.headerBg} border-b-2 ${group.headerBorder}`}>
                      {visibleColumns.find(c => c.id === 'structure') && (
                        <td className="px-5 py-2.5 whitespace-nowrap">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${group.dotClass}`} />
                            <span className={`text-xs font-bold uppercase tracking-wider ${group.textClass}`}>
                              {group.label}
                            </span>
                            <span className="text-[11px] text-slate-400 font-medium">
                              {groupDirectors.length} direction{groupDirectors.length > 1 ? 's' : ''}
                            </span>
                          </div>
                        </td>
                      )}
                      {renderDataCells(groupObjective, groupHR, groupHP, groupTrans)}
                    </tr>
                    {groupDirectors.map((director) => (
                <React.Fragment key={director.id}>
                  {/* ── Director row ── */}
                  <tr
                    draggable
                    onDragStart={() => onDirDragStart(director.id)}
                    onDragOver={(e) => onDirDragOver(e, director.id)}
                    onDrop={(e) => onDirDrop(e, director.id)}
                    onDragEnd={onDirDragEnd}
                    className={`group bg-slate-50 border-b border-slate-200 hover:bg-slate-100/70 transition-colors ${
                      draggingDirId === director.id ? 'opacity-40' : ''
                    } ${dragOverDirId === director.id ? 'border-t-2 border-indigo-400' : ''}`}
                  >
                    {visibleColumns.find(c => c.id === 'structure') && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={selectedItems.has(director.id)}
                            onChange={() => {}}
                            onClick={(e) => toggleSelection(director.id, e)}
                            className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 cursor-pointer shrink-0 accent-indigo-600"
                          />
                          {/* Drag handle */}
                          <GripVertical
                            size={14}
                            className="text-slate-300 group-hover:text-slate-400 cursor-grab active:cursor-grabbing shrink-0 transition-colors"
                          />
                          {director.agencies.length > 0 ? (
                            <button onClick={(e) => { e.stopPropagation(); toggleItem(director.id); }} className="focus:outline-none shrink-0">
                              {expandedItems.has(director.id)
                                ? <ChevronDown className="w-4 h-4 text-slate-400" />
                                : <ChevronRight className="w-4 h-4 text-slate-400" />}
                            </button>
                          ) : <div className="w-4" />}
                          {/* Avatar */}
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${getCompanyBg(director.company)}`}>
                            <span className="text-xs font-bold text-white">{getInitials(director.name)}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-slate-800">{director.name}</span>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${getCompanyBadgeClass(director.company)}`}>
                                {director.company}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedDirector(director); }}
                            className="ml-1 p-1 text-slate-300 hover:text-indigo-500 transition-colors"
                            title="Voir les détails"
                          >
                            <Eye size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                    {renderDataCells(director.annual_objective, director.total_sales, director.potential_sales, director.sales_count)}
                  </tr>

                  {/* ── Agency rows ── */}
                  {expandedItems.has(director.id) && getSortedAgencies(director).map((agency) => (
                    <React.Fragment key={`${director.id}-${agency.name}`}>
                      <tr
                        draggable
                        onDragStart={() => onAgcDragStart(director.id, agency.code)}
                        onDragOver={(e) => onAgcDragOver(e, agency.code)}
                        onDrop={(e) => onAgcDrop(e, director.id, agency.code)}
                        onDragEnd={onAgcDragEnd}
                        className={`group bg-sky-50/60 border-b border-sky-100 hover:bg-sky-100/50 transition-colors ${
                          draggingAgency?.code === agency.code ? 'opacity-40' : ''
                        } ${dragOverAgencyCode === agency.code && draggingAgency?.code !== agency.code ? 'border-t-2 border-indigo-300' : ''}`}
                      >
                        {visibleColumns.find(c => c.id === 'structure') && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2 pl-8">
                              {/* Checkbox */}
                              <input
                                type="checkbox"
                                checked={selectedItems.has(`${director.id}_${agency.code}`)}
                                onChange={() => {}}
                                onClick={(e) => toggleSelection(`${director.id}_${agency.code}`, e)}
                                className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 cursor-pointer shrink-0 accent-indigo-600"
                              />
                              {/* Drag handle */}
                              <GripVertical
                                size={13}
                                className="text-slate-300 group-hover:text-slate-400 cursor-grab active:cursor-grabbing shrink-0 transition-colors"
                              />
                              {agency.negotiators.length > 0 ? (
                                <button onClick={(e) => { e.stopPropagation(); toggleItem(`${director.id}_${agency.name}`); }} className="focus:outline-none shrink-0">
                                  {expandedItems.has(`${director.id}_${agency.name}`)
                                    ? <ChevronDown className="w-4 h-4 text-slate-400" />
                                    : <ChevronRight className="w-4 h-4 text-slate-400" />}
                                </button>
                              ) : <div className="w-4" />}
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${getCompanyBg(director.company)} opacity-80`}>
                                <span className="text-[10px] font-bold text-white">{getInitials(agency.name)}</span>
                              </div>
                              <span className="text-sm font-semibold text-slate-700">{agency.name}</span>
                            </div>
                          </td>
                        )}
                        {renderDataCells(agency.annual_objective, agency.total_sales, agency.potential_sales, agency.sales_count)}
                      </tr>

                      {/* ── Negotiator rows ── */}
                      {expandedItems.has(`${director.id}_${agency.name}`) && agency.negotiators.map(negotiator => {
                        const negKey = `${director.id}_${agency.code}_${negotiator.id}`;
                        return (
                          <tr
                            key={`${director.id}-${agency.name}-${negotiator.id}`}
                            className={`border-b border-slate-100 hover:bg-indigo-50/20 cursor-pointer transition-colors ${
                              selectedItems.has(negKey) ? 'bg-indigo-50/40' : 'bg-white'
                            }`}
                            onClick={() => handleNegotiatorClick(negotiator, agency.name)}
                          >
                            {visibleColumns.find(c => c.id === 'structure') && (
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2 pl-16">
                                  <input
                                    type="checkbox"
                                    checked={selectedItems.has(negKey)}
                                    onChange={() => {}}
                                    onClick={(e) => toggleSelection(negKey, e)}
                                    className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 cursor-pointer shrink-0 accent-indigo-600"
                                  />
                                  <div className="w-7 h-7 rounded-full bg-slate-400 flex items-center justify-center shrink-0">
                                    <span className="text-[10px] font-bold text-white">{getInitials(negotiator.name)}</span>
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-sm text-slate-700">{negotiator.name}</span>
                                    <span className="text-xs text-indigo-400 font-medium">Voir le détail →</span>
                                  </div>
                                </div>
                              </td>
                            )}
                            {renderDataCells(negotiator.annual_objective, negotiator.total_sales, negotiator.potential_sales, negotiator.sales_count)}
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </React.Fragment>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Totaux Honoraires ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mt-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Totaux Honoraires</h3>
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px] flex items-center gap-3 px-4 py-3 rounded-xl bg-indigo-50 border border-indigo-100">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
              <Target size={18} className="text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold uppercase tracking-wide text-indigo-600">Objectif Cumulé</p>
              <p className="text-[11px] text-slate-400">{getObjectiveLabel()}</p>
            </div>
            <span className="text-base font-bold text-indigo-700 whitespace-nowrap">{formatCurrency(totalObjective)}</span>
          </div>

          <div className="flex-1 min-w-[200px] flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-100">
            <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
              <Wallet size={18} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Hono Réalisé</p>
              <p className="text-[11px] text-slate-400">Total exact</p>
            </div>
            <span className="text-base font-bold text-emerald-700 whitespace-nowrap">{formatCurrency(totalHR)}</span>
          </div>

          <div className={`flex-1 min-w-[200px] flex items-center gap-3 px-4 py-3 rounded-xl border ${totalRemaining > 0 ? 'bg-red-50 border-red-100' : 'bg-emerald-50 border-emerald-100'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${totalRemaining > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
              {totalRemaining > 0
                ? <TrendingDown size={18} className="text-red-600" />
                : <TrendingUp size={18} className="text-emerald-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-bold uppercase tracking-wide ${totalRemaining > 0 ? 'text-red-700' : 'text-emerald-700'}`}>Reste à réaliser</p>
              <p className="text-[11px] text-slate-400">Total exact</p>
            </div>
            <span className={`text-base font-bold whitespace-nowrap ${totalRemaining > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
              {formatCurrency(Math.abs(totalRemaining))}
            </span>
          </div>

          {showPotential && (
            <div className="flex-1 min-w-[200px] flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-100">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <Sparkles size={18} className="text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Potentiel</p>
                <p className="text-[11px] text-slate-400">Total exact</p>
              </div>
              <span className="text-base font-bold text-amber-700 whitespace-nowrap">{formatCurrency(totalHP)}</span>
            </div>
          )}

          <div className="flex-1 min-w-[200px] flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200">
            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
              <Receipt size={18} className="text-slate-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-600">Nb Transactions</p>
              <p className="text-[11px] text-slate-400">Sur la période</p>
            </div>
            <span className="text-base font-bold text-slate-700 whitespace-nowrap">{totalTransactions}</span>
          </div>
        </div>
      </div>

      <NegotiatorDetailsModal
        isOpen={selectedNegotiator !== null}
        onClose={() => setSelectedNegotiator(null)}
        negotiatorName={selectedNegotiator?.name ?? ''}
        transactions={transactions}
        agencyName={selectedAgencyName}
      />

      <DirectorDetailsModal
        isOpen={selectedDirector !== null}
        onClose={() => setSelectedDirector(null)}
        director={selectedDirector}
        period={period}
        selectedPeriod={selectedPeriod}
        fiscalYear={fiscalYear}
      />

      {/* ── Floating selection bar ── */}
      {selectedItems.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 bg-[#1e2a4a] text-white rounded-2xl shadow-2xl border border-white/10">
          <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-xs font-bold shrink-0">
            {selectedItems.size}
          </div>
          <span className="text-sm font-medium text-white/90">
            structure{selectedItems.size > 1 ? 's' : ''} sélectionnée{selectedItems.size > 1 ? 's' : ''}
          </span>
          <button
            onClick={() => setShowRecapModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-400 rounded-xl text-xs font-semibold transition-colors"
          >
            <BarChart3 size={13} />
            Voir le récapitulatif
          </button>
          <button
            onClick={() => setSelectedItems(new Set())}
            className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title="Annuler la sélection"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* ── Recap modal ── */}
      {showRecapModal && (() => {
        const items = getRecapItems();
        const totals = items.reduce(
          (acc, item) => ({
            objective: acc.objective + item.objective,
            potential: acc.potential + item.potential,
            sales: acc.sales + item.sales,
            transactions: acc.transactions + item.transactions,
          }),
          { objective: 0, potential: 0, sales: 0, transactions: 0 }
        );
        const cumulative = totals.potential + totals.sales;
        const remaining = totals.objective - totals.sales;
        const completionRate = totals.objective ? (totals.sales / totals.objective) * 100 : 0;

        const kpi = (label: string, value: string | number, sub: string, pillClass: string) => (
          <div className="flex flex-col gap-1 bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">{label}</span>
            <span className={`mt-1 inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold border w-fit ${pillClass}`}>
              {value}
            </span>
            {sub && <span className="text-[10px] text-slate-400 mt-0.5">{sub}</span>}
          </div>
        );

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-slate-50 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">

              {/* Header */}
              <div className="bg-[#1e2a4a] px-6 py-5 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-500/30 flex items-center justify-center">
                    <BarChart3 size={18} className="text-indigo-300" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-white">Récapitulatif</h2>
                    <p className="text-xs text-white/50 mt-0.5">
                      {items.length} structure{items.length > 1 ? 's' : ''} · {getObjectiveLabel()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRecapModal(false)}
                  className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Selected chips */}
              <div className="px-6 py-3 flex flex-wrap gap-2 bg-white border-b border-slate-100 shrink-0">
                {items.map(item => (
                  <span
                    key={item.id}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${getCompanyBadgeClass(item.company)}`}
                  >
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0 ${getCompanyBg(item.company)}`}>
                      {getInitials(item.name)}
                    </span>
                    {item.name}
                    {item.level === 'agency' && <span className="opacity-40 text-[10px]">agence</span>}
                    {item.level === 'negotiator' && <span className="opacity-40 text-[10px]">nég.</span>}
                  </span>
                ))}
              </div>

              {/* KPIs */}
              <div className="p-6 overflow-y-auto">
                <div className="grid grid-cols-3 gap-3 mb-3">
                  {kpi('Objectif', formatCurrency(totals.objective), getObjectiveLabel(), 'border-slate-200 text-slate-600 bg-slate-50')}
                  {kpi('Hono Potentiel', formatCurrency(totals.potential), '', 'border-amber-200 text-amber-700 bg-amber-50')}
                  {kpi('Hono Réalisé', formatCurrency(totals.sales), '', 'border-emerald-200 text-emerald-700 bg-emerald-50')}
                </div>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {kpi('Hono Cumulé', formatCurrency(cumulative), 'potentiel + réalisé', 'border-indigo-200 text-indigo-700 bg-indigo-50')}
                  {kpi(
                    'Reste',
                    `${remaining > 0 ? '↘ ' : '↗ '}${formatCurrency(Math.abs(remaining))}`,
                    '',
                    remaining > 0 ? 'border-red-200 text-red-700 bg-red-50' : 'border-emerald-200 text-emerald-700 bg-emerald-50'
                  )}
                  <div className="flex flex-col gap-1 bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">% Réalisation</span>
                    <span className={`mt-1 inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold border w-fit ${
                      completionRate >= 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      completionRate >= 75  ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      completionRate >= 50  ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                              'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {completionRate.toFixed(1)}%
                    </span>
                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden mt-2">
                      <div
                        className={`h-full rounded-full ${
                          completionRate >= 100 ? 'bg-emerald-400' :
                          completionRate >= 75  ? 'bg-blue-400' :
                          completionRate >= 50  ? 'bg-amber-400' : 'bg-red-400'
                        }`}
                        style={{ width: `${Math.min(completionRate, 100)}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-slate-400 mt-0.5">{totals.transactions} transactions</span>
                  </div>
                </div>

                {/* Detail table */}
                <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Détail par structure</span>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="px-4 py-2 text-left text-slate-400 font-semibold">Structure</th>
                        <th className="px-4 py-2 text-right text-slate-400 font-semibold">Objectif</th>
                        <th className="px-4 py-2 text-right text-slate-400 font-semibold">Réalisé</th>
                        <th className="px-4 py-2 text-right text-slate-400 font-semibold">%</th>
                        <th className="px-4 py-2 text-right text-slate-400 font-semibold">Trans.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => {
                        const rate = item.objective ? (item.sales / item.objective) * 100 : 0;
                        return (
                          <tr key={item.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0 ${getCompanyBg(item.company)}`}>
                                  {getInitials(item.name)}
                                </div>
                                <div>
                                  <div className="font-semibold text-slate-700">{item.name}</div>
                                  <div className="text-[10px] text-slate-400">{item.level === 'director' ? 'Direction' : item.level === 'agency' ? 'Agence' : 'Négociateur'}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-right text-slate-600">{item.objective ? formatCurrency(item.objective) : '—'}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-emerald-700">{item.sales ? formatCurrency(item.sales) : '—'}</td>
                            <td className="px-4 py-2.5 text-right">
                              <span className={`font-bold ${
                                rate >= 100 ? 'text-emerald-600' : rate >= 75 ? 'text-blue-600' : rate >= 50 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {item.objective ? `${rate.toFixed(1)}%` : '—'}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-right text-slate-500">{item.transactions}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}
